'use client';

import { useId, useRef, useState, type FormEvent } from 'react';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { RichText } from '@eliadmin/core/src/components/nt/ui/rich-text';
import {
  CaptchaRequestMessage,
  isCaptchaValidationError,
} from '@eliadmin/core/src/components/providers/captcha-form-message';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type FormState =
  | { status: 'idle' | 'pending' | 'success' }
  | { status: 'invalid' | 'error'; message: string }
  | { status: 'captcha'; error: unknown };

/**
 * Figma newsletter form (46:5216): e-mail + „Odoberať“ in one white field, consent text below. The
 * consent says subscribing means agreeing, so the request carries `gdpr: true` (Figma has no
 * checkbox). Same API, confirmation link and analytics event as the core Newsletter.
 */
export function FooterNewsletter({
  placeholder,
  button,
  consent,
}: {
  /** Page fields „Newsletter - placeholder / tlačidlo / súhlas“ (súhlas = HTML). */
  placeholder: string;
  button: string;
  consent: string;
}) {
  const { t } = useTranslations();
  const systemLabels = useSystemLabels();
  const locale = useLocaleStore(state => state.currentLocale);
  const gtmId = useGlobalStore(state => state.generalLayout.marketing?.gtm_id);
  const inputRef = useRef<HTMLInputElement>(null);
  const messageId = useId();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<FormState>({ status: 'idle' });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (state.status === 'pending' || state.status === 'success') return;
    const value = email.trim();
    if (!value || !inputRef.current?.validity.valid) {
      setState({ status: 'invalid', message: t(value ? 'text732' : 'text731') });
      inputRef.current?.focus();
      return;
    }

    setState({ status: 'pending' });
    try {
      await newsletterSubscribe({
        locale,
        body: {
          email: value,
          gdpr: true,
          return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
        },
      });
      setState({ status: 'success' });
      trackFormSubmitSuccess({
        form_id: 'newsletter',
        form_name: 'newsletter_form',
        form_destination: window.location.href,
        form_length: 1,
        send_to: gtmId ?? undefined,
      });
    } catch (error) {
      if (isCaptchaValidationError(error)) {
        setState({ status: 'captcha', error });
        return;
      }
      // A 422 on the e-mail field is a problem of the value; anything else is a server error.
      const fieldMessage = error instanceof ValidationError ? error.fields.email?.join(' ') : '';
      setState(
        fieldMessage
          ? { status: 'invalid', message: fieldMessage }
          : { status: 'error', message: t('text573') },
      );
    }
  };

  const isInvalid = state.status === 'invalid';
  const message = state.status === 'invalid' || state.status === 'error' ? state.message : '';

  return (
    <form
      noValidate
      onSubmit={submit}
      className="flex w-full max-w-[652px] flex-col items-center gap-4"
    >
      <div
        className={cn(
          'flex w-full max-w-[426px] items-center gap-3 rounded-2xl bg-white py-1 pl-5 pr-1 shadow-soft sm:gap-6',
          'has-[input:focus-visible]:shadow-focus',
          isInvalid &&
            'shadow-[0_0_0_2px_#D92D20] has-[input:focus-visible]:shadow-[0_0_0_2px_#D92D20]',
        )}
      >
        <input
          ref={inputRef}
          type="email"
          name="email"
          required
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={event => {
            setEmail(event.target.value);
            if (state.status !== 'pending' && state.status !== 'success') {
              setState({ status: 'idle' });
            }
          }}
          disabled={state.status === 'success'}
          placeholder={placeholder || t('text183')}
          aria-label={systemLabels('email')}
          aria-invalid={isInvalid || undefined}
          aria-describedby={isInvalid ? messageId : undefined}
          className="nt-body-l h-12 min-w-0 flex-1 bg-transparent text-[#0D110D] outline-none placeholder:text-[#8F978E] disabled:text-[#5D655D]"
        />
        <Button
          type="submit"
          isLoading={state.status === 'pending'}
          disabled={state.status === 'success'}
        >
          {button || t('text182')}
        </Button>
      </div>
      {/* Always in the tree so the confirmation is announced; out of the flow while empty. */}
      <p
        role="status"
        className={
          state.status === 'success'
            ? 'nt-body-m-semibold flex items-center gap-2 text-[#0E6A20]'
            : 'sr-only'
        }
      >
        {state.status === 'success' && (
          <>
            <Icon name="check-circle" />
            {t('text3139')}
          </>
        )}
      </p>
      {message && (
        <p id={messageId} role="alert" className="nt-body-m text-center text-[#B42318]">
          {message}
        </p>
      )}
      {state.status === 'captcha' && <CaptchaRequestMessage error={state.error} />}
      <RichText
        html={consent}
        variant="nt-body-m"
        links="inherit"
        className="text-center text-[#5D655D]"
      />
    </form>
  );
}
